import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useReactToPrint } from "react-to-print";
import { Box } from "@mui/material";

import { PageLayout } from "../../layouts/PageLayout";
import { CardLayout, Invoice, PageHeader } from "../../components/v3";
import { DownloadAndPrint } from "../../components/DownloadAndPrint";
import { get_date } from "../../components/generalComponets";
import { getAccountName, getDateAndTime } from "../../utils/formatters";
import { AccountService, FinanceService } from "../../services";

interface UserInfo {
  firstName: string;
  lastName: string;
}

interface SubcontractorReceiptData {
  date?: string;
  createdBy?: UserInfo;
  createdAt?: string;
  updatedBy?: UserInfo;
  updatedAt?: string;
  toAccountName?: string;
  fromAccountName?: string;
  amount?: string | number;
  description?: string;
  transaction?: {
    fromAccountId: string;
    toAccountId: string;
  };
}

export const SubcontractorReceipt = () => {
  const [invoiceData, setInvoiceData] = useState<Partial<SubcontractorReceiptData>>({});

  const { paymentId } = useParams();
  const printRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchData(paymentId);
  }, [paymentId]);

  const fetchData = async (id) => {
    try {
      const { data: response } = await FinanceService.getExpense(id);
      let invoicePayload = { ...response };

      const accountIds = Array.from(
        new Set([response?.transaction.fromAccountId, response?.transaction.toAccountId].filter(Boolean)),
      );

      if (accountIds.length > 0) {
        const { data: accounts } = await AccountService.getAccounts({
          ids: accountIds.join(","),
          limit: accountIds.length,
          includeDeleted: true,
          includeOwnerDetails: true,
          includeRestrictedTypes: true,
        });

        const fromAccount = accounts.rows.find((account) => account?.id === response?.transaction?.fromAccountId);
        const toAccount = accounts.rows.find((account) => account?.id === response?.transaction?.toAccountId);

        invoicePayload = {
          ...invoicePayload,
          fromAccountName: getAccountName(fromAccount?.type, fromAccount),
          toAccountName: getAccountName(toAccount?.type, toAccount),
        };
      }
      setInvoiceData(invoicePayload);
    } catch (error) {
      toast.error("Failed to fetch receipt data.");
      console.error("Error fetching receipt data:", error);
    }
  };

  const handlePrint = useReactToPrint({
    contentRef: printRef,
  });
  return (
    <PageLayout>
      {/* header section */}
      <Box sx={{ position: "relative", zIndex: 1 }}>
        <PageHeader title="Payment Receipt" />
      </Box>

      {/* table section */}
      <Box sx={{ mt: -9, zIndex: 2, position: "relative" }}>
        <CardLayout>
          <div ref={printRef}>
            <Invoice
              title="Payment Receipt"
              sectionA={[{ label: "Date", value: get_date(invoiceData?.date, "DD-MM-YYYY") }]}
              sectionB={[
                {
                  label: "Created By",
                  value: invoiceData?.createdBy
                    ? `${invoiceData?.createdBy?.firstName} ${invoiceData?.createdBy?.lastName} (${getDateAndTime(
                        invoiceData?.createdAt,
                      )})`
                    : "N/A",
                },
                invoiceData?.updatedBy && {
                  label: "Last Updated By",
                  value: `${invoiceData?.updatedBy?.firstName ?? ""} ${invoiceData?.updatedBy?.lastName ?? ""} (${getDateAndTime(
                    invoiceData?.updatedAt,
                  )})`,
                },
              ].filter(Boolean)}
              sectionC={[
                { label: "Payment To", value: invoiceData?.toAccountName },
                { label: "Received From", value: invoiceData?.fromAccountName },
              ]}
              sectionD={[
                { label: "Amount", value: invoiceData?.amount ? `₹ ${invoiceData?.amount}` : "-" },
                { label: "Remarks", value: invoiceData?.description ? invoiceData.description : "N/A" },
                {
                  label: "",
                  value: "Authorized Signatory",
                },
              ]}
            />
          </div>
        </CardLayout>
      </Box>

      <DownloadAndPrint handlePrint={handlePrint} printRef={printRef} fileName={"SubcontractorReceipt.pdf"} />
    </PageLayout>
  );
};
